import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowUpRight,
  AtSign,
  Bell,
  CheckCheck,
  ChevronDown,
  ListChecks,
  type LucideIcon,
  MailOpen,
  Send,
  User,
  UserCheck,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router";
import { toast } from "sonner";
import { ApiError, api } from "../api";
import { useSession } from "../auth";
import { CommentItem } from "../components/issues/activity";
import { MentionField } from "../components/issues/mention-field";
import {
  PRIORITY_LABELS,
  PriorityIcon,
  StatusDisc,
  TypeIcon,
  UserChip,
  useIssuesMeta,
} from "../components/issues/meta";
import { EmptyState, Eyebrow, PageSkeleton, SectionHeader } from "../components/page";
import { TimeAgo } from "../components/time-ago";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Tabs } from "../components/ui/tabs";
import { ViewTab, ViewTabsList } from "../components/view-tabs";
import { useMediaQuery } from "../hooks/use-media-query";
import { useIsMobile } from "../hooks/use-mobile";
import type { IssueModalState } from "../lib/issue-modal";
import { personLabel } from "../lib/people";
import { cn } from "../lib/utils";
import type { InboxComment, InboxReason, InboxResponse, UserBadgeInfo } from "../types";

export function useInbox() {
  return useQuery({
    queryKey: ["inbox"],
    queryFn: api.inbox,
    refetchInterval: 30_000,
  });
}

const PAGE_SIZE = 20;

/** Every comment this person was notified about, newest first, a page at a time. */
function useInboxHistory() {
  return useInfiniteQuery({
    queryKey: ["inbox", "comments"],
    queryFn: ({ pageParam }) => api.inboxComments({ limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, _pages, lastOffset) =>
      lastOffset + PAGE_SIZE < last.total ? lastOffset + PAGE_SIZE : undefined,
    refetchInterval: 30_000,
  });
}

type Category = "all" | InboxReason;

const CATEGORIES: Array<{ value: Category; label: string }> = [
  { value: "all", label: "All" },
  { value: "mention", label: "Mentions" },
  { value: "reporter", label: "Reported" },
  { value: "reviewer", label: "Reviewing" },
];

const REASON_ICON: Record<InboxReason, LucideIcon> = {
  mention: AtSign,
  reporter: User,
  reviewer: UserCheck,
};

const ROLE_LABEL = { reporter: "Reporter", reviewer: "Reviewer" } as const;

/**
 * Two panes. The left lists what you were notified about, with the unread ones marked;
 * the right previews the selected conversation and lets you reply without leaving. On
 * narrow screens the preview takes the list's place until you go back.
 */
export function InboxPage() {
  const inbox = useInbox();
  const history = useInboxHistory();
  const meta = useIssuesMeta().data;
  const queryClient = useQueryClient();
  const location = useLocation();
  const isMobile = useIsMobile();
  const twoPane = useMediaQuery("(min-width: 80rem)");
  const modalState: IssueModalState | undefined = isMobile ? undefined : { backgroundLocation: location };
  const [category, setCategory] = useState<Category>("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const unread = useMemo(() => inbox.data?.comments ?? [], [inbox.data]);
  const unreadIds = useMemo(() => new Set(unread.map((comment) => comment.id)), [unread]);
  const all = useMemo(() => {
    const seen = new Set<string>();
    const out: InboxComment[] = [];
    for (const page of history.data?.pages ?? []) {
      for (const comment of page.comments) {
        if (seen.has(comment.id)) continue;
        seen.add(comment.id);
        out.push(comment);
      }
    }
    return out;
  }, [history.data]);
  const matches = (comment: InboxComment) => category === "all" || comment.reasons.includes(category);
  const rows = (unreadOnly ? unread : all).filter(matches);
  const unreadCount = (value: Category) =>
    unread.filter((comment) => value === "all" || comment.reasons.includes(value)).length;
  const selected = useMemo(
    () => all.find((comment) => comment.id === selectedId) ?? unread.find((c) => c.id === selectedId) ?? null,
    [all, unread, selectedId],
  );

  const markSeen = useMutation({
    mutationFn: (refs: string[]) => Promise.all(refs.map((ref) => api.markInboxSeen(ref))),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["inbox"] }),
  });
  // Reading a notification is what marks it seen, like opening the issue does.
  const selectedUnreadRef = selected && unreadIds.has(selected.id) ? selected.issue.identifier : null;
  const markSeenMutate = markSeen.mutate;
  useEffect(() => {
    if (selectedUnreadRef) markSeenMutate([selectedUnreadRef]);
  }, [selectedUnreadRef, markSeenMutate]);

  if (inbox.isLoading) return <PageSkeleton />;
  if (inbox.isError || !inbox.data) {
    return (
      <EmptyState icon={Bell} title="Could not load notifications" description="Try again in a moment." />
    );
  }

  const users = meta?.users ?? [];
  const showPreview = twoPane || selected !== null;
  const showList = twoPane || selected === null;

  return (
    <div
      className={cn(
        // Fills the viewport under the header so each pane scrolls on its own.
        "-mx-gutter -mt-5 -mb-16 flex h-[calc(100dvh-3rem-1px-env(safe-area-inset-top)-var(--tab-bar-h))] min-h-0 overflow-hidden md:-mt-7 md:h-[calc(100dvh-4rem)]",
        twoPane && "grid grid-cols-[minmax(0,26rem)_minmax(0,1fr)]",
      )}
    >
      {showList && (
        <section aria-label="Notifications" className="flex min-h-0 min-w-0 flex-col border-r">
          <div className="flex items-center gap-3 px-gutter pt-4">
            <h1 className="text-xl leading-tight font-semibold tracking-[-0.015em]">Notifications</h1>
            <span className="flex-1" />
            <Label className="cursor-pointer gap-2 text-xs font-normal text-muted-foreground">
              <Switch size="sm" checked={unreadOnly} onCheckedChange={setUnreadOnly} />
              Unread
            </Label>
            {unread.length > 0 && (
              <Button
                variant="ghost"
                size="icon-sm"
                className="-mr-1.5 text-muted-foreground"
                aria-label="Mark all as read"
                title="Mark all as read"
                disabled={markSeen.isPending}
                onClick={() =>
                  markSeen.mutate([...new Set(unread.map((comment) => comment.issue.identifier))])
                }
              >
                <CheckCheck />
              </Button>
            )}
          </div>
          <div className="mt-1 border-b px-gutter">
            <Tabs value={category} onValueChange={(value) => setCategory(value as Category)}>
              <ViewTabsList className="gap-4">
                {CATEGORIES.map((entry) => {
                  const count = unreadCount(entry.value);
                  return (
                    <ViewTab key={entry.value} value={entry.value} className="h-9">
                      {entry.label}
                      {count > 0 && (
                        <span className="text-xs text-muted-foreground tabular-nums">{count}</span>
                      )}
                    </ViewTab>
                  );
                })}
              </ViewTabsList>
            </Tabs>
          </div>
          <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto">
            {history.isLoading && !unreadOnly ? (
              <RowsSkeleton />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={Bell}
                title="You're all caught up"
                description={
                  unreadOnly
                    ? "No unread notifications in this category."
                    : "New mentions and comments on issues you report or review will show up here."
                }
                className="m-4 mt-10 border-0"
              />
            ) : (
              <ul className="divide-y divide-border/60">
                {rows.map((comment) => (
                  <NotificationRow
                    key={comment.id}
                    comment={comment}
                    unread={unreadIds.has(comment.id)}
                    selected={comment.id === selectedId}
                    onSelect={() => setSelectedId(comment.id)}
                    onMarkRead={() => markSeen.mutate([comment.issue.identifier])}
                  />
                ))}
              </ul>
            )}
            {!unreadOnly && history.hasNextPage && (
              <Button
                variant="ghost"
                size="sm"
                className="my-2 w-full text-muted-foreground"
                disabled={history.isFetchingNextPage}
                onClick={() => void history.fetchNextPage()}
              >
                <ChevronDown className="size-3.5" />
                {history.isFetchingNextPage ? "Loading…" : "Show older"}
              </Button>
            )}
          </div>
        </section>
      )}

      {showPreview && (
        <section aria-label="Conversation" className="flex min-h-0 min-w-0 flex-col overflow-y-auto">
          {selected ? (
            <ConversationPreview
              key={selected.id}
              comment={selected}
              users={users}
              modalState={modalState}
              onBack={twoPane ? undefined : () => setSelectedId(null)}
            />
          ) : (
            <YourIssues issues={inbox.data.issues} modalState={modalState} />
          )}
        </section>
      )}
    </div>
  );
}

function RowsSkeleton() {
  return (
    <div className="divide-y divide-border/60">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-start gap-3 px-gutter py-3">
          <Skeleton className="size-6 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** One notification: who did what on which issue, a line of the comment, and when. */
function NotificationRow({
  comment,
  unread,
  selected,
  onSelect,
  onMarkRead,
}: {
  comment: InboxComment;
  unread: boolean;
  selected: boolean;
  onSelect: () => void;
  onMarkRead: () => void;
}) {
  const mentioned = comment.reasons.includes("mention");
  const Reason = REASON_ICON[comment.reasons[0] ?? "reviewer"];
  return (
    <li
      className={cn(
        "group/row relative flex items-start gap-3 px-gutter py-3 transition-colors duration-(--motion-dur) hover:bg-muted/50",
        unread && "bg-primary/[0.035]",
        selected && "bg-muted/60",
      )}
    >
      {unread && (
        <span className="absolute top-[1.3rem] left-2 size-1.5 rounded-full bg-primary" aria-hidden />
      )}
      <button
        type="button"
        onClick={onSelect}
        aria-label={`${unread ? "Unread: " : ""}${personLabel(comment.author, "Someone")} on ${comment.issue.identifier}`}
        className="flex min-w-0 flex-1 items-start gap-3 text-left"
      >
        <Avatar className="mt-0.5 size-6 shrink-0">
          <AvatarFallback className="text-[10px]">{initials(comment.author)}</AvatarFallback>
        </Avatar>
        <span className="min-w-0 flex-1 text-[13px] leading-snug">
          <span className="block">
            <span className="font-semibold">{personLabel(comment.author, "Someone")}</span>{" "}
            <span className="text-muted-foreground">{mentioned ? "mentioned you in" : "commented on"}</span>{" "}
            <span className="font-medium">{comment.issue.title}</span>
          </span>
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">{snippet(comment.body)}</span>
          <span className="mt-1 flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
            <Reason className="size-3" />
            <span className="font-mono">{comment.issue.identifier}</span>
            <span aria-hidden>·</span>
            <TimeAgo at={comment.createdAt} className="tabular-nums" />
          </span>
        </span>
      </button>
      {unread && (
        <Button
          variant="ghost"
          size="icon-sm"
          className="-mr-1.5 size-6 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/row:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
          aria-label="Mark as read"
          title="Mark as read"
          onClick={onMarkRead}
        >
          <MailOpen className="size-3.5" />
        </Button>
      )}
    </li>
  );
}

/** The selected notification's issue: its facts, the recent conversation, and a reply box. */
function ConversationPreview({
  comment,
  users,
  modalState,
  onBack,
}: {
  comment: InboxComment;
  users: UserBadgeInfo[];
  modalState: IssueModalState | undefined;
  onBack?: () => void;
}) {
  const identifier = comment.issue.identifier;
  const meta = useIssuesMeta().data;
  const issue = useQuery({ queryKey: ["issue", identifier], queryFn: () => api.issue(identifier) });
  const comments = useQuery({
    queryKey: ["issue-comments", identifier],
    queryFn: () => api.issueComments(identifier),
    refetchInterval: 15_000,
  });
  const detail = issue.data?.issue;
  const status = meta?.statuses.find((s) => s.key === detail?.statusKey);
  const type = meta?.types.find((t) => t.key === detail?.typeKey);
  const recent = (comments.data?.comments ?? []).slice(-6);

  return (
    <>
      <div className="flex h-11 shrink-0 items-center gap-2 border-b px-4">
        {onBack && (
          <Button variant="ghost" size="icon-sm" className="-ml-1.5" aria-label="Back" onClick={onBack}>
            <ArrowLeft />
          </Button>
        )}
        <StatusDisc category={status?.category} color={status?.color} />
        <span className="text-xs text-muted-foreground">{status?.name ?? detail?.statusKey ?? "…"}</span>
        <span className="font-mono text-xs text-muted-foreground/70">{identifier}</span>
        <span className="flex-1" />
        <Button variant="outline" size="sm" asChild>
          <Link to={`/issues/${identifier}`} state={modalState}>
            Open issue <ArrowUpRight className="size-3.5" />
          </Link>
        </Button>
      </div>
      <div className="fx-rise w-full max-w-3xl px-6 py-6">
        <h2 className="text-xl leading-tight font-semibold tracking-[-0.015em] text-balance">
          {detail?.title ?? comment.issue.title}
        </h2>
        {detail && (
          <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="flex items-center gap-1.5">
              <PriorityIcon priority={detail.priority} />
              {PRIORITY_LABELS[detail.priority]}
            </span>
            <span className="flex items-center gap-1.5">
              <TypeIcon typeKey={detail.typeKey} color={type?.color} />
              {type?.name ?? detail.typeKey}
            </span>
            <span className="flex items-center gap-1.5">
              <UserChip user={detail.reviewer} />
              <span className="text-muted-foreground">{detail.reviewer ? "reviews" : "No reviewer"}</span>
            </span>
          </div>
        )}
        <Eyebrow className="mt-6 mb-1">Conversation</Eyebrow>
        {comments.isLoading ? (
          <div className="space-y-3 py-2">
            <Skeleton className="h-4 w-2/5" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-4 w-1/3" />
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {recent.map((entry) => (
              <CommentItem
                key={entry.id}
                comment={entry}
                users={users}
                className={cn(
                  "-mx-2.5 px-2.5",
                  entry.id === comment.id && "rounded-md bg-primary/[0.04] first:pt-3",
                )}
              />
            ))}
          </ul>
        )}
        <ReplyBox identifier={identifier} users={users} />
      </div>
    </>
  );
}

function ReplyBox({ identifier, users }: { identifier: string; users: UserBadgeInfo[] }) {
  const queryClient = useQueryClient();
  const session = useSession();
  const [draft, setDraft] = useState("");
  const post = useMutation({
    mutationFn: () => api.addComment(identifier, { body: draft.trim() }),
    onSuccess: () => {
      setDraft("");
      void queryClient.invalidateQueries({ queryKey: ["issue-comments", identifier] });
      void queryClient.invalidateQueries({ queryKey: ["inbox"] });
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Reply failed"),
  });
  const submit = () => {
    if (draft.trim() && !post.isPending) post.mutate();
  };
  return (
    <form
      className="mt-4 flex gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <Avatar className="mt-1.5 size-6">
        <AvatarFallback className="text-[10px]">
          {initials({ displayName: session.name, email: session.email })}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1 rounded-lg border border-input bg-card shadow-xs transition-[border-color,box-shadow] duration-(--motion-dur) focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/30">
        <MentionField
          value={draft}
          onChange={setDraft}
          onSubmit={submit}
          users={users}
          disabled={post.isPending}
          placeholder="Reply… use @ to mention"
        />
        <div className="flex items-center justify-between gap-2 px-3 pb-2">
          <span className="hidden text-xs text-muted-foreground pointer-fine:inline">
            <kbd className="font-sans">⌘</kbd>
            <kbd className="font-sans">↵</kbd> to send
          </span>
          <Button type="submit" size="sm" className="ml-auto" disabled={!draft.trim() || post.isPending}>
            <Send className="size-3.5" />
            {post.isPending ? "Sending…" : "Reply"}
          </Button>
        </div>
      </div>
    </form>
  );
}

/** What the preview pane shows while nothing is selected: the open issues you report or review. */
function YourIssues({
  issues,
  modalState,
}: {
  issues: InboxResponse["issues"];
  modalState: IssueModalState | undefined;
}) {
  const meta = useIssuesMeta().data;
  if (issues.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <EmptyState
          icon={ListChecks}
          title="Select a notification"
          description="Read the conversation and reply without leaving your inbox."
          className="w-full max-w-sm border-0"
        />
      </div>
    );
  }
  return (
    <div className="fx-rise w-full max-w-3xl px-6 py-6">
      <SectionHeader
        title="Your open issues"
        count={issues.length}
        description="Issues you report or review, until they're done"
        className="mb-1"
      />
      <ul className="-mx-2 divide-y divide-border/60">
        {issues.map((issue) => {
          const status = meta?.statuses.find((row) => row.key === issue.statusKey);
          return (
            <li key={issue.identifier}>
              <Link
                to={`/issues/${issue.identifier}`}
                state={modalState}
                className="group/row flex min-h-9 items-center gap-2.5 px-2 py-1.5 text-sm transition-colors duration-(--motion-dur) hover:bg-muted/50"
              >
                <StatusDisc category={status?.category} color={status?.color} />
                <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                  {issue.identifier}
                </span>
                <span className="min-w-0 truncate decoration-border underline-offset-[3px] group-hover/row:underline">
                  {issue.title}
                </span>
                <span className="ml-auto flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                  <span className="hidden sm:inline">
                    {issue.roles.map((role) => ROLE_LABEL[role]).join(" · ")}
                  </span>
                  <TimeAgo at={issue.updatedAt} className="tabular-nums" />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function initials(user: { displayName: string; email: string } | null | undefined): string {
  return personLabel(user, "?")
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** One plain line of a markdown comment, for the row under the notification. */
function snippet(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[*_`>~]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
